import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import "./IsfSiteSelector.scss";

function resolveSiteMeta(siteRows = [], siteId) {
  if (!siteId) return null;
  return (
    (siteRows || []).find(
      (row) =>
        String(row.SiteId || "").toLowerCase() === String(siteId).toLowerCase(),
    ) || null
  );
}

function resolveSiteAbbr(siteName, siteAbbr, siteNumber) {
  const abbr = String(siteAbbr || "").trim();
  if (abbr) return abbr.toUpperCase();
  const number = String(siteNumber || "").trim();
  if (number) return number.toUpperCase();
  return getInitials(siteName, 2);
}

function buildSiteDisplay(option, siteRows) {
  const row = resolveSiteMeta(siteRows, option?.value);
  const siteName = row?.SiteName || option?.label || "Site";
  const siteNumber = row?.SiteNumber || "";
  const siteAbbrValue = row?.SiteAbbr || "";
  const label = siteNumber ? `${siteName} (${siteNumber})` : siteName;

  return {
    siteId: option?.value || row?.SiteId || "",
    siteName,
    siteNumber,
    siteAbbr: resolveSiteAbbr(siteName, siteAbbrValue, siteNumber),
    label,
    searchText: `${siteName} ${siteNumber} ${siteAbbrValue} ${label}`.toLowerCase(),
  };
}

function SiteBadge({ siteName, siteAbbr, size = "md" }) {
  return (
    <span
      className={`isf-site-picker__badge isf-site-picker__badge--${size}`}
      style={{ backgroundColor: getAvatarColor(siteName) }}
      title={siteAbbr}
      aria-hidden="true"
    >
      {siteAbbr}
    </span>
  );
}

function SiteRow({ site, isActive, onSelect }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      className={`isf-site-picker__option${isActive ? " is-active" : ""}`}
      onClick={() => onSelect?.({ value: site.siteId, label: site.label })}
      title={site.label}
    >
      <SiteBadge
        siteName={site.siteName}
        siteAbbr={site.siteAbbr}
        size="sm"
      />
      <span className="isf-site-picker__option-body">
        <span className="isf-site-picker__name">{site.siteName}</span>
      </span>
      {isActive ? (
        <span className="isf-site-picker__check" aria-hidden="true">
          <i className="fas fa-check" />
        </span>
      ) : null}
    </button>
  );
}

export default function IsfSiteSelector({
  options = [],
  siteRows = [],
  selectedSiteId = null,
  onSelect,
  onOverviewClick,
  showOverviewActive = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPos, setMenuPos] = useState({});
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const sites = useMemo(
    () =>
      (options || [])
        .map((opt) => buildSiteDisplay(opt, siteRows))
        .filter((site) => site.siteId),
    [options, siteRows],
  );

  const activeSite = useMemo(() => {
    if (!sites.length) return null;
    return (
      sites.find(
        (site) =>
          String(site.siteId).toLowerCase() ===
          String(selectedSiteId || "").toLowerCase(),
      ) || sites[0]
    );
  }, [sites, selectedSiteId]);

  const filteredSites = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sites;
    return sites.filter((site) => site.searchText.includes(term));
  }, [sites, search]);

  const isMultiSite = sites.length > 1;
  const showSearch = sites.length >= 5;

  const updateMenuPos = useCallback(() => {
    if (!triggerRef.current) return;
    const EDGE_GAP = 8;
    const PREFERRED_MENU_HEIGHT = 320;
    const MIN_MENU_HEIGHT = 160;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - EDGE_GAP;
    const spaceAbove = rect.top - EDGE_GAP;
    let openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;
    let usableSpace = openUpward ? spaceAbove : spaceBelow;

    if (usableSpace < MIN_MENU_HEIGHT) {
      openUpward = spaceAbove > spaceBelow;
      usableSpace = Math.max(spaceAbove, spaceBelow);
    }

    const menuMaxHeight = Math.max(
      MIN_MENU_HEIGHT,
      Math.min(PREFERRED_MENU_HEIGHT, usableSpace),
    );

    setMenuPos({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      maxHeight: menuMaxHeight,
      openUpward,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  const openMenu = useCallback(() => {
    if (!isMultiSite) return;
    updateMenuPos();
    setIsOpen(true);
  }, [isMultiSite, updateMenuPos]);

  const handleSelect = useCallback(
    (opt) => {
      closeMenu();
      onSelect?.(opt);
    },
    [closeMenu, onSelect],
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocClick = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inRoot && !inMenu) closeMenu();
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };

    const onLayout = () => updateMenuPos();

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [isOpen, closeMenu, updateMenuPos]);

  useEffect(() => {
    if (isOpen && showSearch) {
      searchRef.current?.focus();
    }
  }, [isOpen, showSearch]);

  if (!sites.length && !selectedSiteId) return null;
  if (!activeSite) return null;

  const menu = isOpen
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={`isf-site-picker__menu${
            menuPos.openUpward ? " is-upward" : ""
          }`}
          style={{
            position: menuPos.position,
            left: menuPos.left,
            width: menuPos.width,
            zIndex: menuPos.zIndex,
            ...(menuPos.openUpward
              ? { bottom: menuPos.bottom }
              : { top: menuPos.top }),
          }}
          role="listbox"
          aria-label="Clinical sites"
        >
          {showSearch ? (
            <div className="isf-site-picker__search-wrap">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                ref={searchRef}
                type="search"
                className="isf-site-picker__search"
                placeholder="Search sites…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Search clinical sites"
              />
              {search ? (
                <button
                  type="button"
                  className="isf-site-picker__search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" />
                </button>
              ) : null}
            </div>
          ) : (
            <div className="isf-site-picker__menu-head">
              <span>Select site</span>
              <span className="isf-site-picker__menu-count">{sites.length}</span>
            </div>
          )}

          <div
            className="isf-site-picker__menu-list"
            style={{ maxHeight: Math.max(120, (menuPos.maxHeight || 280) - 56) }}
          >
            {filteredSites.length ? (
              filteredSites.map((site) => (
                <SiteRow
                  key={site.siteId}
                  site={site}
                  isActive={
                    String(site.siteId).toLowerCase() ===
                    String(activeSite.siteId).toLowerCase()
                  }
                  onSelect={handleSelect}
                />
              ))
            ) : (
              <div className="isf-site-picker__empty">No sites match your search.</div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div
      className={`isf-site-picker-row${isOpen ? " is-open" : ""}${
        showOverviewActive ? " is-overview-active" : ""
      }`}
      ref={rootRef}
      data-tour="isf-site-selector"
    >
      <div className="isf-site-picker isf-site-picker-row__select">
        <button
          ref={triggerRef}
          type="button"
          className={`isf-site-picker__trigger${
            isMultiSite ? " is-clickable" : " is-static"
          }${isOpen ? " is-open" : ""}`}
          onClick={openMenu}
          aria-haspopup={isMultiSite ? "listbox" : undefined}
          aria-expanded={isMultiSite ? isOpen : undefined}
          disabled={!isMultiSite}
          title={activeSite.label}
        >
          <SiteBadge
            siteName={activeSite.siteName}
            siteAbbr={activeSite.siteAbbr}
            size="sm"
          />
          <span className="isf-site-picker__trigger-body">
            <span className="isf-site-picker__line">
              <span className="isf-site-picker__name">{activeSite.siteName}</span>
              {isMultiSite ? (
                <span className="isf-site-picker__meta">
                  {" "}
                  · {sites.length} sites
                </span>
              ) : null}
            </span>
          </span>
          {isMultiSite ? (
            <span className="isf-site-picker__trigger-action" aria-hidden="true">
              <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`} />
            </span>
          ) : null}
        </button>
        {menu}
      </div>
      {onOverviewClick ? (
        <button
          type="button"
          className={`isf-site-picker__overview${
            showOverviewActive ? " is-active" : ""
          }`}
          data-tour="isf-site-overview-btn"
          onClick={onOverviewClick}
          title="Site overview and open tasks"
          aria-label="Site overview"
          aria-pressed={showOverviewActive}
        >
          <i className="fas fa-grip-horizontal" aria-hidden="true" />
          <span className="isf-site-picker__overview-label">Overview</span>
        </button>
      ) : null}
    </div>
  );
}
